import type { CreatePaymentBody } from '@klopotun/contracts';
import { formatCivilDate, toCivilDate, type CivilDate } from '@klopotun/domain';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMeQuery } from '../../entities/me/index.js';
import { useSavePaymentMutation } from '../../entities/payment/index.js';
import {
  emptyPaymentFormValues,
  PaymentForm,
  type PaymentFormStep,
} from '../../features/payment-form/index.js';
import { useBackButton } from '../../shared/telegram/index.js';
import { ErrorState, Screen, Spinner } from '../../shared/ui/index.js';
import styles from './payment-create-page.module.css';

const FIRST_STEP: PaymentFormStep = 'main';

type PaymentCreateFormProps = {
  today: CivilDate;
  baseCurrency: string;
  step: PaymentFormStep;
  onStepChange: (step: PaymentFormStep) => void;
};

const PaymentCreateForm = ({ today, baseCurrency, step, onStepChange }: PaymentCreateFormProps) => {
  const [initialValues] = useState(() => emptyPaymentFormValues(today, baseCurrency));
  const navigate = useNavigate();
  const savePayment = useSavePaymentMutation();

  const save = (body: CreatePaymentBody): void => {
    savePayment.mutate(
      { mode: 'create', body },
      {
        onSuccess: () => {
          void navigate(-1);
        },
      },
    );
  };

  return (
    <>
      {savePayment.error !== null && (
        <p className={styles.saveError} role="alert">
          {savePayment.error.message}
        </p>
      )}

      <PaymentForm
        initialValues={initialValues}
        step={step}
        onStepChange={onStepChange}
        onSubmit={save}
        busy={savePayment.isPending}
        today={formatCivilDate(today)}
      />
    </>
  );
};

/**
 * The steps walked so far, newest last. "Back" retraces them rather than guessing the previous
 * one, because whether the past-payments step sits in between depends on the date typed on the
 * first step.
 */
export const PaymentCreatePage = () => {
  const [steps, setSteps] = useState<PaymentFormStep[]>([FIRST_STEP]);
  const navigate = useNavigate();
  const me = useMeQuery();
  const goBack = useCallback(() => {
    if (steps.length > 1) {
      setSteps(steps.slice(0, -1));
      return;
    }

    void navigate(-1);
  }, [steps, navigate]);

  useBackButton(goBack);

  if (me.isPending) {
    return (
      <Screen>
        <div className={styles.centered}>
          <Spinner label="Завантаження" />
        </div>
      </Screen>
    );
  }

  if (me.isError) {
    return (
      <Screen>
        <ErrorState
          title="Не вдалося відкрити форму"
          message={me.error.message}
          onRetry={() => {
            void me.refetch();
          }}
        />
      </Screen>
    );
  }

  const { baseCurrency, timezone } = me.data.settings;

  return (
    <Screen>
      <div className={styles.content}>
        <h1 className={styles.title}>Нова підписка</h1>

        <PaymentCreateForm
          today={toCivilDate(new Date(), timezone)}
          baseCurrency={baseCurrency}
          step={steps.at(-1) ?? FIRST_STEP}
          onStepChange={(next) => {
            setSteps([...steps, next]);
          }}
        />
      </div>
    </Screen>
  );
};
